import React from 'react';

import { CheckRounded, Close, ContentCopyRounded } from '@mui/icons-material';
import { Alert, AlertTitle, IconButton, Stack, Typography } from '@mui/material';
import type { CustomContentProps } from 'notistack';
import { useSnackbar } from 'notistack';

import { useBoolean } from '@looko-corp/acloset-react-kit/src/react';

export interface AclosetServiceErrorSnackbarProps extends CustomContentProps {
  serverMessage?: string;
  errorCode?: string;
}

/**
 * API 에러 발생 시 서버 메시지와 에러 코드를 함께 표시하는 스낵바.
 *
 * - 기존 하드코딩 문구(message)는 굵게 표시
 * - 서버 message가 있으면 보조 텍스트로 표시
 * - 서버 code가 있으면 모노스페이스로 표시
 * - 복사 버튼: serverMessage·errorCode 블록 아래 우측에 위치, 둘 다 클립보드에 복사
 *
 * @see src/types/notistack.d.ts — variant 타입 선언
 * @see useEnqueueAclosetServiceErrorSnackbar — 에러에서 데이터 추출 후 이 컴포넌트 호출하는 훅
 */
export const AclosetServiceErrorSnackbar = React.forwardRef<HTMLDivElement, AclosetServiceErrorSnackbarProps>(
  function AclosetServiceErrorSnackbar(props, ref) {
    const { id, message, serverMessage, errorCode, style, className } = props;
    const { closeSnackbar } = useSnackbar();
    const { value: copied, setTrue: setCopied, setFalse: clearCopied } = useBoolean(false);
    const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // [CODE] 메시지 형태의 표시 텍스트 (복사 내용과 동일)
    const detailText = [errorCode && `[${errorCode}]`, serverMessage].filter(Boolean).join(' ');

    const handleCopy = async () => {
      if (!detailText) return;

      try {
        await navigator.clipboard.writeText(detailText);
        setCopied();
        // 기존 타이머가 있으면 취소하고 새로 시작
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(clearCopied, 2000);
      } catch {
        // 클립보드 접근 불가 — 조용히 무시
      }
    };

    // 언마운트 시 타이머 정리
    React.useEffect(() => {
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }, []);

    const hasDetails = serverMessage || errorCode;

    return (
      <Alert
        ref={ref}
        severity="error"
        variant="filled"
        style={style}
        className={className}
        sx={{ width: '100%', minWidth: 280, maxWidth: 400 }}
        action={
          <IconButton size="small" color="inherit" aria-label="닫기" onClick={() => closeSnackbar(id)}>
            <Close fontSize="small" />
          </IconButton>
        }
      >
        <AlertTitle sx={{ fontWeight: 'bold', mb: hasDetails ? 0.5 : 0 }}>{message}</AlertTitle>

        {/* [CODE] 메시지 텍스트와 복사 버튼을 한 줄로 표시 */}
        {hasDetails && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" sx={{ opacity: 0.9, flex: 1, userSelect: 'all' }}>
              {detailText}
            </Typography>
            <IconButton size="small" color="inherit" aria-label="에러 정보 복사" onClick={handleCopy} sx={{ p: 0.25 }}>
              {copied ? <CheckRounded sx={{ fontSize: 13 }} /> : <ContentCopyRounded sx={{ fontSize: 13 }} />}
            </IconButton>
          </Stack>
        )}
      </Alert>
    );
  },
);
